import { FC, memo, ReactNode, useCallback, useMemo, useState } from "react";
import { IEvents } from "../../../domain/models/Events";
import PopupMenu from "../../modals/PopupMenu";
import { useTranslation } from "react-i18next";
import DeleteTrashIconSVG from "../../../custom-components/svg/functional-icons/DeleteTrashIconSVG";
import EyeIconSVG from "../../shared/svg/console/EyeIconSVG";
import BaseModal from "../../modals/BaseModal";
import EditPencilIconSVG from "../../../custom-components/svg/functional-icons/EditPencilIconSVG";
import ImagePlaceholderSVG from "../../shared/svg/ImagePlaceholder";

interface EventsCardProps {
    item: IEvents;
    onClick?: () => void;
    onDelete?: (id: string) => void;
    onEdit?: (id: string) => void;
}

const EventsCard: FC<EventsCardProps> = ({ item, onClick, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const eventDate = useMemo(() => new Date(item.eventDate).toLocaleDateString(), [item.eventDate]);

    const handleCopyId = useCallback(() => {
        navigator.clipboard.writeText(item.id).catch(() => {
            console.error("Failed to copy ID to clipboard");
        });
    }, [item.id]);

    const handleDelete = useCallback(() => {
        onDelete?.(item.id);
        setDeleteModalVisible(false);
    }, [onDelete, item.id]);

    const openDeleteModal = useCallback(() => setDeleteModalVisible(true), []);

    const MenuItem: FC<{ onClick: () => void; className?: string; children: ReactNode }> = ({
        onClick,
        className,
        children,
    }) => (
        <button
            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${className ?? ""}`}
            onClick={onClick}
        >
            {children}
        </button>
    );

    return (
        <div
            className="bg-white dark:bg-dark-bg-100 rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition relative"
            onClick={onClick}
        >
            <div className="flex justify-center">
                {item.pictureExists() ? (
                    <img
                        src={item.getPictureUrl()}
                        alt={item.translations[0].title}
                        className="w-full h-40 object-cover"
                    />
                ) : (
                    <ImagePlaceholderSVG fill="#bec6d5" classname="h-40 justify-self-center" />
                )}
            </div>

            <div className="p-4">
                <h2 className="text-lg font-semibold text-light-text-100 dark:text-dark-text-100 mb-2 truncate">
                    {item.translations[0].title}
                </h2>
                <span className="text-xs text-gray-400">{eventDate}</span>
            </div>
            <div className="absolute top-2 right-2 z-10">
                <PopupMenu
                    trigger={
                        <button className="px-3 py-1 text-light-text-100 dark:text-dark-text-100 bg-light-bg-200 dark:bg-dark-bg-700 rounded-lg hover:bg-gray-200 hover:dark:bg-dark-bg-900 shadow">
                            ⋮
                        </button>
                    }
                >
                    <MenuItem
                        onClick={onClick}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-1">
                            <EyeIconSVG className="size-5" /> {t("console.events.view")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={() => onEdit(item.id)}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.events.edit")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={handleCopyId}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.events.copy")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={openDeleteModal}
                        className="text-red-500 hover:bg-red-100 hover:dark:bg-dark-bg-600"
                    >
                        <div className="flex items-center gap-2">
                            <DeleteTrashIconSVG iconColor="red" /> {t("console.events.delete")}
                        </div>
                    </MenuItem>
                </PopupMenu>
            </div>

            <BaseModal
                isVisible={isDeleteModalVisible}
                setIsVisible={setDeleteModalVisible}
                title={t("console.events.deleteConfirmTitle")}
                confirmTitle={t("console.events.delete")}
                cancelTitle={t("console.common.cancel")}
                confirmHandler={handleDelete}
                additionalWrapClassName="w-[400px]"
                additionalConfirmButtonClassName="bg-red-600 hover:bg-red-700"
            >
                <div className="text-center py-4">
                    <p className="text-lg text-light-text-100 dark:text-dark-text-100 mb-2">
                        {t("console.events.deleteConfirmText")}
                    </p>
                    <p className="text-sm text-gray-500">{item.translations[0].title}</p>
                </div>
            </BaseModal>
        </div>
    );
};

export default memo(EventsCard);
